import { Box } from "@rebass/grid";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import {
  EIntakeStep,
  IApplication,
  IApplicationFlag,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  DisplayText,
  ESpacings,
  Modal,
  Shimmer,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import { useMediaQuery } from "@rentiohq/web-shared-next/dist/themes";
import styled from "@rentiohq/web-theme";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  isCandidacySubmission,
  shouldShowIncome,
} from "utils/application.utils";
import { Rating } from "../../../../../components/Rating";
import * as t from "../../../../../services/translationService";
import { RM_COLORS } from "../rentmatch.colors";
import type { SortDirection } from "./ApplicationTable";
import { ApplicationInfoIndicators } from "./ApplicationTable/applicationIndicators";
import { getApplicationIndicators } from "./ApplicationTable/applicationIndicators.utils";
import { HeaderCell } from "./ApplicationTable/ApplicationTable.styled";
import { SortIndicator } from "./ApplicationTable/ApplicationTableHeader";
import { FlagLabel } from "./FlagLabel";
import { NoShowLabel } from "./NoShowLabel";
import {
  DEFAULT_OTHER_APPLICATIONS_SORT,
  sortOtherApplications,
  TOtherApplicationsSortColumn,
} from "./otherApplicationsModal.utils";
import { WarningTooltip } from "./WarningTooltip";

export interface IOtherApplicationProperty {
  applicationId: string;
  propertyId: number | null;
  propertyAddress: string;
  step: number;
  stepCompletedAt: string | null;
  stepStartedAt: string | null;
}

export interface IOtherApplicationNote {
  applicationId: string;
  content: string | null;
  createdAt: string | Date;
}

interface IProps {
  candidateName: string;
  /** The office's mark on this candidate; it covers every row below. */
  flag?: IApplicationFlag | null;
  properties: IOtherApplicationProperty[];
  /** Notes across the broker's own properties, matched to a row by id. */
  notes?: IOtherApplicationNote[];
  onClose: () => void;
}

// Fixed layout, so the columns take their widths from the colgroup below and
// not from what is in the cells. With the automatic layout every column
// resized the moment the placeholders made way for the values, and the whole
// table shifted sideways under the broker's eyes (DEV-3183).
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

// The header, the rows and their text follow the candidate list rather than
// this dialog's own taste: same size, same weight, same gray, no uppercase and
// no letter-spacing, a 2px rule under the header and a 1px rule under each row.
// A broker reads the same facts in both places, so they should not be styled
// like two different products (DEV-3122).
const Th = styled.th`
  padding: 12px 16px 12px 0;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral90};
  background: ${({ theme }) => theme.colors.neutral0};
  border-bottom: 2px solid ${({ theme }) => theme.getColor("gray", 200)};

  &:last-child {
    padding-right: 0;
  }
`;

const Tr = styled.tr<{ isClickable?: boolean }>`
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};

  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
  }

  &:hover td {
    background: ${({ theme }) => theme.colors.neutral10};
  }
`;

const Td = styled.td`
  padding: 16px 16px 16px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.neutral90};
  vertical-align: middle;

  &:last-child {
    padding-right: 0;
  }
`;

// The property stands where the candidate's name stands in the list, so it
// takes that treatment.
const AddressText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutral100};
`;

interface IStatusBadgeProps {
  step: number;
  isArchived?: boolean;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  waitingList: { bg: RM_COLORS.yellowBg, color: RM_COLORS.yellowDark },
  notEligible: { bg: RM_COLORS.redBg, color: RM_COLORS.redDark },
  candidateTenant: { bg: RM_COLORS.greenBgSoft, color: RM_COLORS.greenDark },
  tenant: { bg: RM_COLORS.greenBgSoft, color: RM_COLORS.greenDark },
  visitor: { bg: RM_COLORS.blueBgAlt, color: RM_COLORS.blueDark },
  invited: { bg: RM_COLORS.blueBgAlt, color: RM_COLORS.blueDark },
  planAsVisitor: { bg: RM_COLORS.skyBg, color: RM_COLORS.blueDeep },
  default: { bg: RM_COLORS.gray100, color: RM_COLORS.gray700 },
};

const getStatusColors = (step: number, isArchived?: boolean) => {
  // An archived candidacy is not in a phase any more, so it gets the neutral
  // treatment rather than the color of wherever it stood before (DEV-3122).
  if (isArchived) {
    return STATUS_COLORS.default;
  }

  if (step === EIntakeStep.waitingList) return STATUS_COLORS.waitingList;
  if (step === EIntakeStep.notEligible) return STATUS_COLORS.notEligible;
  if (step === EIntakeStep.candidateTenant)
    return STATUS_COLORS.candidateTenant;
  if (step === EIntakeStep.tenant) return STATUS_COLORS.tenant;
  if (step === EIntakeStep.visitor) return STATUS_COLORS.visitor;
  if (step === EIntakeStep.invited) return STATUS_COLORS.invited;
  if (step === EIntakeStep.planAsVisitor) return STATUS_COLORS.planAsVisitor;
  return STATUS_COLORS.default;
};

// Every measurable property taken from the pill in the candidate list: the
// same display, the same gap, the same 3px above it, the same 2px 8px padding,
// 11px at weight 600, the same fully rounded corners, and fit-content so it
// hugs its text instead of filling the column.
//
// One deliberate difference, and the two now say different things with color.
// The list uses it for milestone versus phase: green where the candidate
// delivered something, gray where the label only names where they stand
// (DEV-3182). Here the rows span several properties at once, so the color has
// to carry the phase itself, which is why this scheme stays per phase.
const StatusBadge = styled.span<IStatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
  width: fit-content;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 9999px;
  background-color: ${({ step, isArchived }) =>
    getStatusColors(step, isArchived).bg};
  color: ${({ step, isArchived }) => getStatusColors(step, isArchived).color};
`;

// Stands in for a line of text while it loads: it keeps the text itself, made
// invisible, so it takes exactly the box the text will take, and lays the
// shimmer over that box. A placeholder sized by hand was always a pixel or two
// off the real line, and that difference is what made the rows jump (DEV-3183).
const TextPlaceholder = styled.span`
  position: relative;
  display: inline-block;
  color: transparent;

  & > * {
    position: absolute;
    inset: 0;
  }
`;

// The status badge with its color taken away, so the shimmer can lie over the
// exact pill the badge will draw: same padding, same size, same margin.
const StatusBadgePlaceholder = styled(StatusBadge)`
  position: relative;
  background-color: transparent;
  color: transparent;

  & > * {
    position: absolute;
    inset: 0;
  }
`;

// Date over status, the way the Activity column reads in the list.
const ActivityCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const DateText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.neutral90};
`;

// The same chevron the candidate list puts at the end of a row: a bordered
// button in gray, not a bare blue arrow. Rounded on all four corners here,
// where the list rounds only the right side because it sits attached to the
// action buttons beside it.
// The row's actions, right-aligned and all of one height, so a no-show label
// and the chevron read as one control rather than two things that happen to be
// next to each other.
const RowActions = styled.div`
  display: inline-flex;
  align-items: stretch;
  justify-content: flex-end;
  height: 32px;
`;

const NavigateButton = styled.button<{ $isAttached?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  line-height: 1;
  border: 1px solid ${({ theme }) => theme.colors.neutral40};
  /* Rounded on all four corners on its own; only on the right once a label is
     joined to it, whose one-pixel border this then covers. */
  border-radius: ${({ $isAttached }) => ($isAttached ? "0 6px 6px 0" : "6px")};
  margin-left: ${({ $isAttached }) => ($isAttached ? "-1px" : "0")};
  background: ${({ theme }) => theme.colors.neutral0};
  color: ${({ theme }) => theme.colors.neutral70};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 120ms ease;

  &:hover {
    background: ${({ theme }) => theme.colors.neutral10};
  }

  &:disabled {
    cursor: default;
    opacity: 0.3;
  }
`;

const AddressCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IncomeText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral100};
`;

const formatActivityDate = (property: IOtherApplicationProperty): string => {
  const dateValue = property.stepCompletedAt ?? property.stepStartedAt;
  if (!dateValue) {
    return "-";
  }

  return utils.date.format(new Date(dateValue), "dd/MM/yyyy HH:mm");
};

/**
 * One row of the dialog: the property, and what this candidate delivered there.
 *
 * The values are fetched per row rather than carried on the cross-property
 * meta, which only holds an address, a step and two timestamps. Fetching them
 * means they come from the same serializer the candidate list reads, so the
 * rules agreed on DEV-3075, DEV-3107, DEV-3114, DEV-3115 and DEV-3135 apply
 * here by construction instead of being restated (DEV-3122).
 *
 * Nothing here is red on account of the match score. That score belongs to a
 * property, and every row is a different one; decided by Jonas on 2026-09-06.
 * The document fraction still reddens, since that is not score-based.
 */
const getStatusLabel = (params: {
  step: number;
  isArchived: boolean;
  showsSubmittedLabel: boolean;
}) => {
  const { step, isArchived, showsSubmittedLabel } = params;

  if (isArchived) {
    return getLocalizedText("applications.tab.archived");
  }

  if (showsSubmittedLabel) {
    return getLocalizedText("rentmatch.applications.state.submitted");
  }

  return t.applicationStateTitleRentmatch(step as EIntakeStep);
};

/**
 * Where this candidacy stands now, as a pill. Shared by the row and the card,
 * so the two layouts cannot come to name a phase differently (DEV-3542).
 *
 * Waits on the fetch: both the archived treatment and the submitted wording
 * come from the application, so without the placeholder the badge would show
 * the old phase and then change under the broker's eyes. The placeholder
 * carries the phase label the meta already gives, so it is the badge the row
 * will most likely end up with.
 */
const OtherApplicationStatus = (props: {
  property: IOtherApplicationProperty;
  application?: IApplication;
  isLoading: boolean;
}) => {
  const { property, application, isLoading } = props;

  if (isLoading) {
    return (
      <StatusBadgePlaceholder step={property.step} aria-hidden={true}>
        {t.applicationStateTitleRentmatch(property.step as EIntakeStep)}
        <Shimmer width="100%" height="100%" radius={9999} />
      </StatusBadgePlaceholder>
    );
  }

  // Archived wins over the phase: the broker is being told where this
  // candidacy is now, and it is in the archive, not in the column it sat in
  // before.
  const isArchived = !!application?.archivedAt;

  // Same rule as the candidate list: the pill replaces the phase name only
  // where the submission itself was a candidacy and the row stands on the
  // candidate-tenant step.
  const showsSubmittedLabel =
    property.step === EIntakeStep.candidateTenant &&
    isCandidacySubmission(application?.submittedStep);

  return (
    <StatusBadge step={property.step} isArchived={isArchived}>
      {getStatusLabel({
        step: property.step,
        isArchived,
        showsSubmittedLabel,
      })}
    </StatusBadge>
  );
};

/**
 * The full applications behind the cross-property meta, in one request for
 * every row instead of one per row (DEV-3183). The meta already carries the
 * address, the phase and the dates; only income, household, documents, rating
 * and the archived flag need the whole application, and fetching those
 * separately meant five requests for five other properties, staggered so the
 * rows filled in one at a time.
 *
 * Still full applications through the same serializer, so these rows render the
 * same record as the candidate list and every rule about counters and phases
 * holds by construction (DEV-3122).
 */
const useOtherApplications = (properties: IOtherApplicationProperty[]) => {
  const applicationIds = React.useMemo(
    () => properties.map(property => property.applicationId),
    [properties],
  );

  const { items: applications, fetchError } = applicationHooks.useGetAll({
    shouldRefetch: false,
    query: applicationIds.length
      ? { filter: { id: { in: applicationIds } } }
      : undefined,
  });

  // Derived from the absence of data, not from isFetching. The hook reports
  // isFetching as false until the request has actually been dispatched, which
  // happens in an effect, so the first painted frame would otherwise claim it
  // is done while nothing has arrived. That frame is exactly the one that used
  // to show a zero-star rating.
  //
  // A failed request ends the wait too: leaving the placeholders up forever
  // would be a worse lie than an empty cell.
  const isLoading =
    applicationIds.length > 0 && applications === undefined && !fetchError;

  const applicationsById = React.useMemo(
    () =>
      new Map(
        (applications ?? []).map(application => [application.id, application]),
      ),
    [applications],
  );

  return { applicationsById, isLoading };
};

const OtherApplicationRow = (props: {
  property: IOtherApplicationProperty;
  notes: IOtherApplicationNote[];
  /**
   * Fetched once for every row by the dialog rather than per row (DEV-3183).
   * Undefined while that request is in flight, which is what the placeholders
   * below stand in for.
   */
  application?: IApplication;
  isLoading: boolean;
  onNavigate: (propertyId: number, step: number | "archived") => void;
}) => {
  const { property, notes, application, isLoading, onNavigate } = props;

  // Archived is not a phase, it is a flag on the application, so it does not
  // travel with the cross-property meta. It arrives with the fetch, the same
  // payload the candidate list reads it from.
  const isArchived = !!application?.archivedAt;

  // Not clickable until that has arrived. Where a row sends you depends on
  // whether the candidacy is archived, and until we know, a click would send a
  // broker to the phase tab for someone who is in the archive.
  const canNavigate = property.propertyId != null && !isLoading;
  const navigateToProperty = () => {
    if (property.propertyId != null) {
      onNavigate(property.propertyId, isArchived ? "archived" : property.step);
    }
  };

  const indicators = application
    ? getApplicationIndicators(application)
    : undefined;

  // The same no-show the candidate list reads off the candidacy (DEV-3223),
  // from the bundled fetch this row already does, so the two screens agree by
  // construction rather than by two lookups that can drift.
  const noShowAt = application?.noShowAt;
  const hasNoShow = !!noShowAt;

  return (
    // The whole row navigates, not just the chevron at the end of it. The
    // chevron stays as the affordance that says so, and stops the click from
    // reaching the row so the navigation does not fire twice.
    <Tr
      isClickable={canNavigate}
      onClick={canNavigate ? navigateToProperty : undefined}
    >
      <Td>
        <AddressCell>
          <AddressText>{property.propertyAddress || "-"}</AddressText>
          {notes.length > 0 && (
            <WarningTooltip
              size={16}
              tooltipContent={
                <div style={{ maxWidth: "240px" }}>
                  {notes.map((note, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && (
                        <hr
                          style={{
                            border: "none",
                            borderTop: `1px solid ${RM_COLORS.amberBorder}`,
                            margin: "5px 0",
                          }}
                        />
                      )}
                      <div style={{ marginBottom: "4px" }}>{note.content}</div>
                    </React.Fragment>
                  ))}
                </div>
              }
            />
          )}
        </AddressCell>
      </Td>
      <Td>
        <ActivityCell>
          <DateText>{formatActivityDate(property)}</DateText>
          <OtherApplicationStatus
            property={property}
            application={application}
            isLoading={isLoading}
          />
        </ActivityCell>
      </Td>
      {/* These three wait on the fetch, so they get a placeholder shaped like
          the value that is coming rather than an empty cell that fills in
          later. Each one is exactly as tall as that value, so the row does not
          jump (DEV-3183). */}
      <Td>
        {isLoading ? (
          <IncomeText aria-hidden={true}>
            <TextPlaceholder>
              {formatCurrency(1000)}
              <Shimmer width="100%" height="100%" />
            </TextPlaceholder>
          </IncomeText>
        ) : (
          indicators &&
          shouldShowIncome(indicators.totalIncome) && (
            <IncomeText>{formatCurrency(indicators.totalIncome)}</IncomeText>
          )
        )}
      </Td>
      <Td>
        {/* One line of indicators: each is a 16px icon beside its number. */}
        {isLoading ? (
          <Shimmer width={120} height={16} />
        ) : (
          application && <ApplicationInfoIndicators application={application} />
        )}
      </Td>
      <Td>
        {/* The shimmer replaces the stars rather than sitting beside them:
            Rating falls back to 0, so a loading row used to show an actual
            zero-star rating, which reads as "rated zero" and not as "not here
            yet". */}
        {isLoading ? (
          <Shimmer width={80} />
        ) : (
          application && (
            <Rating value={application.rating ?? 0} size={14} disabled={true} />
          )
        )}
      </Td>
      <Td style={{ textAlign: "right" }}>
        {/* Said on the row rather than inside the triangle, and in the place
            the candidate list puts its own no-show: at the end of the row,
            against the chevron. This row is one property, so a warning that
            only repeats that property's own no-show warns about nothing the
            broker cannot already see. What is left in the triangle are the
            notes colleagues left on this candidacy. */}
        <RowActions>
          {hasNoShow && <NoShowLabel noShowAt={noShowAt!} variant="attached" />}
          <NavigateButton
            $isAttached={hasNoShow}
            disabled={!canNavigate}
            onClick={event => {
              event.stopPropagation();
              navigateToProperty();
            }}
          >
            <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="1 1 7 6 1 11" />
            </svg>
          </NavigateButton>
        </RowActions>
      </Td>
    </Tr>
  );
};

/**
 * The width under which the table gives way to one card per candidacy
 * (DEV-3542). Below it the six columns squeeze into nothing; a phone held
 * upright is the case this is for, a tablet held upright is just past it.
 */
const NARROW_DIALOG_QUERY = "(max-width: 767px)";

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// The row's facts stacked for a phone: a hairline box, the same 14px text
// and gray as the table cells.
const Card = styled.div<{ isClickable: boolean }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.neutral90};
  overflow-wrap: anywhere;
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-weight: 600;
`;

const CardLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 4px;
`;

// Spelled out under the facts: the row keeps these behind its triangle, and a
// tooltip needs a mouse.
const CardNote = styled.div`
  font-style: italic;
  white-space: pre-line;
  color: ${({ theme }) => theme.colors.neutral70};
`;

/**
 * One candidacy as a card, for a screen too narrow for the table (DEV-3542).
 * The same facts the row shows, from the same application and the same
 * components, and the same tap: the whole card leads to that property.
 */
const OtherApplicationCard = (props: {
  property: IOtherApplicationProperty;
  notes: IOtherApplicationNote[];
  application?: IApplication;
  isLoading: boolean;
  onNavigate: (propertyId: number, step: number | "archived") => void;
}) => {
  const { property, notes, application, isLoading, onNavigate } = props;

  // Same rules as the row: where the card leads depends on the archived flag,
  // so it waits for the fetch that brings it.
  const isArchived = !!application?.archivedAt;
  const canNavigate = property.propertyId != null && !isLoading;
  const navigateToProperty = () => {
    if (property.propertyId != null) {
      onNavigate(property.propertyId, isArchived ? "archived" : property.step);
    }
  };

  const indicators = application
    ? getApplicationIndicators(application)
    : undefined;
  const noShowAt = application?.noShowAt;

  return (
    <Card
      isClickable={canNavigate}
      onClick={canNavigate ? navigateToProperty : undefined}
    >
      <CardHeader>
        <span>{property.propertyAddress || "-"}</span>
        {noShowAt && <NoShowLabel noShowAt={noShowAt} />}
      </CardHeader>

      <CardLine>
        <OtherApplicationStatus
          property={property}
          application={application}
          isLoading={isLoading}
        />
        <DateText>{formatActivityDate(property)}</DateText>
      </CardLine>

      {isLoading ? (
        <Shimmer width={200} height={16} />
      ) : (
        application && (
          <CardLine>
            {indicators && shouldShowIncome(indicators.totalIncome) && (
              <IncomeText>{formatCurrency(indicators.totalIncome)}</IncomeText>
            )}
            <ApplicationInfoIndicators application={application} />
            <Rating value={application.rating ?? 0} size={14} disabled={true} />
          </CardLine>
        )
      )}

      {notes.map((note, index) => (
        <CardNote key={index}>&quot;{note.content}&quot;</CardNote>
      ))}
    </Card>
  );
};

export const OtherApplicationsModal = (props: IProps) => {
  const { candidateName, flag, properties, notes = [], onClose } = props;
  const navigate = useNavigate();
  const isNarrow = useMediaQuery(NARROW_DIALOG_QUERY);

  const { applicationsById, isLoading: isLoadingApplications } =
    useOtherApplications(properties);

  // Sorted here, on what the dialog already holds, the way the candidate list
  // sorts its own rows: a header click costs no request (DEV-3523). Same
  // behavior as the list, too: a new column starts ascending, a second click
  // on it reverses.
  const [sortColumn, setSortColumn] =
    React.useState<TOtherApplicationsSortColumn>(
      DEFAULT_OTHER_APPLICATIONS_SORT.column,
    );
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(
    DEFAULT_OTHER_APPLICATIONS_SORT.direction,
  );

  const handleSort = (column: TOtherApplicationsSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedProperties = React.useMemo(
    () =>
      sortOtherApplications(
        properties,
        applicationsById,
        sortColumn,
        sortDirection,
      ),
    [properties, applicationsById, sortColumn, sortDirection],
  );

  const renderSortableHeader = (
    column: TOtherApplicationsSortColumn,
    label: string,
  ) => (
    <Th>
      <HeaderCell sortable={true} onClick={() => handleSort(column)}>
        {label}{" "}
        <SortIndicator
          isSorted={sortColumn === column}
          direction={sortDirection}
        />
      </HeaderCell>
    </Th>
  );

  const handleNavigate = (propertyId: number, step: number | "archived") => {
    onClose();
    // The applications screen takes its tab from the router state, so the
    // broker lands on the phase this candidacy is in rather than on the first
    // tab. Step 0 means the meta had no phase row to report, and then the
    // screen decides for itself.
    navigate(`/properties/${propertyId}/applications`, {
      state: step === "archived" || step > 0 ? { step } : undefined,
    });
  };

  return (
    // Same shape as every other dialog in the dashboard: a plain heading, a
    // dismiss, and the content directly in the body rather than inside a card
    // of its own. Wider than the usual large, because this one is a table and
    // its rows wrapped at 960px (DEV-3122). On a narrow screen the cards below
    // replace the table, sorted the way it opens: newest activity first.
    <Modal
      width={isNarrow ? "small" : "extraLarge"}
      heading={getLocalizedText("application.other_applications.modal.heading")}
      onClose={onClose}
      hasDismiss={true}
      shouldCloseOnOverlayClick={true}
    >
      <Box mb={ESpacings.loose}>
        <DisplayText size="medium" element="h3">
          {getLocalizedText("system.candidate")}: {candidateName}
        </DisplayText>
        {/* Once, next to the name, not on every row: the mark is about the
            person, whichever of these properties it was set from. */}
        {flag && (
          <Box mt={ESpacings.tight}>
            <FlagLabel flag={flag} variant="large" />
          </Box>
        )}
      </Box>
      {isNarrow ? (
        <CardList>
          {sortedProperties.map(property => (
            <OtherApplicationCard
              key={property.applicationId}
              property={property}
              application={applicationsById.get(property.applicationId)}
              isLoading={isLoadingApplications}
              notes={notes.filter(
                note => note.applicationId === property.applicationId,
              )}
              onNavigate={handleNavigate}
            />
          ))}
        </CardList>
      ) : (
        <Table>
          {/* The property takes what is left. The rest is sized for its widest
            value: a no-show label beside the chevron in the last column, and
            a full row of indicators in More info. */}
          <colgroup>
            <col />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "28%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>
          <thead>
            <tr>
              {renderSortableHeader(
                "property",
                getLocalizedText(
                  "application.other_applications.modal.column.property",
                ),
              )}
              {renderSortableHeader(
                "activity",
                getLocalizedText("activity_table.info.heading"),
              )}
              {renderSortableHeader(
                "income",
                getLocalizedText("intake.financial.title"),
              )}
              {/* Not sortable, as in the candidate list (DEV-3523). */}
              <Th>{getLocalizedText("system.more_info")}</Th>
              {renderSortableHeader(
                "rating",
                getLocalizedText("property.rating"),
              )}
              <Th />
            </tr>
          </thead>
          <tbody>
            {sortedProperties.map(property => (
              <OtherApplicationRow
                key={property.applicationId}
                property={property}
                application={applicationsById.get(property.applicationId)}
                isLoading={isLoadingApplications}
                notes={notes.filter(
                  note => note.applicationId === property.applicationId,
                )}
                onNavigate={handleNavigate}
              />
            ))}
          </tbody>
        </Table>
      )}
    </Modal>
  );
};
